import type { EnrichedTurn } from './schema';

// Canonical enriched-turn fixtures for the frozen Pangea document and legacy
// voice-history read projection. Durable content lives only in `messages`;
// these fixtures keep adapter/read consumers aligned without defining another
// stored representation. Every fixture parses against `enrichedTurnSchema`.

/**
 * One enriched turn in the current projection shape: a role, at least one content
 * block, an absolute Unix `timestamp`, and the per-turn provider metadata
 * (`interrupted` on agent turns, `source_medium` on user turns) written as
 * explicit nulls rather than omitted.
 */
export function canonicalEnrichedTurn(overrides: Partial<EnrichedTurn> = {}): EnrichedTurn {
  return {
    role: 'user',
    content: [{ type: 'text', text: 'I want to get through Friday without drinking.' }],
    timestamp: 1_767_225_600,
    interrupted: null,
    source_medium: null,
    ...overrides,
  };
}

/**
 * A short two-party transcript — the smallest thing that still exercises what a
 * reader has to get right: role alternation, ordering, and the agent-side
 * `interrupted` flag.
 */
export function canonicalEnrichedTranscript(): EnrichedTurn[] {
  return [
    canonicalEnrichedTurn({
      role: 'agent',
      content: [{ type: 'text', text: 'How did the week go?' }],
      timestamp: 1_767_225_600,
      interrupted: false,
    }),
    canonicalEnrichedTurn({
      role: 'user',
      content: [{ type: 'text', text: 'I want to get through Friday without drinking.' }],
      timestamp: 1_767_225_612,
      source_medium: 'audio',
    }),
  ];
}

/**
 * A turn whose content mixes a text block with tool traffic. Stored transcripts
 * carry all three block types, but only text is member/agent speech — a reader
 * projecting turns into a prompt must drop the rest rather than render a tool
 * call as dialogue, so every such reader needs this case.
 */
export function canonicalToolUsingTurn(): EnrichedTurn {
  return canonicalEnrichedTurn({
    role: 'agent',
    content: [
      { type: 'text', text: 'Logging that for you.' },
      { type: 'tool_use', id: 'toolu_01', name: 'log_insight', input: { kind: 'commitment' } },
    ],
    interrupted: false,
  });
}
